// Enhanced Friendship Module Card with image headers.
// No points or streak bonuses on the card (removed October 2026,
// docs/REVIEW_2026-10.md Decision 1): it says what the module is, how long it
// takes, and whether you have finished it.
import React from 'react';
import { motion } from 'framer-motion';
import {
  Clock, Lock, CheckCircle, ArrowRight,
  Play, Target,
} from 'lucide-react';

const EnhancedTeenModuleCard = ({
  module,
  isCompleted = false,
  canAccess = true,
  progress = 0,
  onClick,
  isRecommended = false,
  unlockHint = null,
  showImage = true,
}) => {
  const getLevelGradient = (level) => {
    const gradients = {
      Foundation: 'from-emerald-500 to-teal-500',
      Essential: 'from-blue-500 to-indigo-500',
      Advanced: 'from-purple-500 to-pink-500',
      Mastery: 'from-red-500 to-orange-500',
      'Life Design': 'from-orange-500 to-pink-500',
    };
    return gradients[level] || gradients.Foundation;
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: canAccess ? 1.02 : 1, y: canAccess ? -4 : 0 }}
      transition={{ duration: 0.2 }}
      className="relative group"
    >
      {/* Recommendation Badge */}
      {isRecommended && (
        <motion.div
          initial={{ scale: 0, rotate: -12 }}
          animate={{ scale: 1, rotate: -12 }}
          className="absolute -top-2 -right-2 z-10 bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-3 py-1 rounded-full text-xs font-bold shadow-lg"
        >
          ✨ RECOMMENDED
        </motion.div>
      )}

      <div
        onClick={canAccess ? onClick : undefined}
        className={`
          relative overflow-hidden rounded-2xl border transition-all duration-300 h-full
          ${canAccess
            ? 'bg-gradient-to-br from-slate-800/60 to-slate-900/80 border-slate-700/50 hover:border-purple-500/50 cursor-pointer shadow-lg hover:shadow-purple-500/20'
            : 'bg-slate-900/40 border-slate-800 cursor-not-allowed'
          }
          backdrop-blur-xl
        `}
      >
        {/* Module Image Header */}
        {showImage && module.image && (
          <div className="relative h-56 overflow-hidden">
            <img
              src={module.image}
              alt={module.imageAlt || `${module.title} illustration`}
              className={`w-full h-full transition-all duration-300 ${
                canAccess ? 'group-hover:scale-110' : 'opacity-50'
              }`}
              style={{
                objectFit: 'cover',
                objectPosition: 'center center',
              }}
              loading="lazy"
            />
            {/* Gradient overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />

            {/* Status icon overlay */}
            <div className="absolute top-3 right-3">
              {isCompleted ? (
                <div className="w-8 h-8 bg-green-500/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg border border-green-400/50">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
              ) : progress > 0 ? (
                <div className="w-8 h-8 bg-yellow-500/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg border border-yellow-400/50">
                  <Target className="w-5 h-5 text-white" />
                </div>
              ) : canAccess ? (
                <div className="w-8 h-8 bg-purple-500/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg border border-purple-400/50">
                  <Play className="w-5 h-5 text-white" />
                </div>
              ) : (
                <div className="w-8 h-8 bg-slate-600/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg border border-slate-500/50">
                  <Lock className="w-5 h-5 text-white" />
                </div>
              )}
            </div>

            {/* Module number badge */}
            <div className="absolute top-3 left-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getLevelGradient(module.level)} shadow-lg flex items-center justify-center backdrop-blur-sm border border-white/20`}>
                <span className="text-white text-sm font-bold">{module.id}</span>
              </div>
            </div>

            {/* Module info overlay at bottom of image */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r ${getLevelGradient(module.level)} text-white font-medium backdrop-blur-sm`}>
                  {module.level}
                </span>
                <span className="text-xs px-2 py-1 rounded-full bg-slate-800/80 text-slate-200 backdrop-blur-sm flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {module.duration}
                </span>
              </div>

              <h3 className="text-lg font-bold text-white mb-1 leading-tight">
                {module.title}
              </h3>
              <p className="text-sm text-purple-200 font-medium">
                {module.subtitle}
              </p>
            </div>

            {/* Progress bar */}
            {progress > 0 && (
              <div className="absolute bottom-0 left-0 right-0 h-1">
                <div className="w-full h-full bg-slate-700/50">
                  <motion.div
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Module Content Below Image */}
        <div className="p-6">
          {/* Description */}
          <p className="text-sm text-slate-300 mb-4 line-clamp-3">
            {module.description}
          </p>

          {/* Cool Factor */}
          {module.coolFactor && (
            <div className="text-xs text-cyan-300 bg-cyan-500/10 px-3 py-2 rounded-lg border border-cyan-500/20 mb-4">
              ✨ {module.coolFactor}
            </div>
          )}

          {/* Unlock Hint for locked modules */}
          {unlockHint && !canAccess && (
            <div className="text-xs text-orange-300 bg-orange-500/10 px-3 py-2 rounded-lg border border-orange-500/20 mb-4">
              🔒 {unlockHint}
            </div>
          )}

          {/* Bottom section */}
          <div className="flex items-center justify-end text-xs">
            {/* Action indicator */}
            <div className="flex items-center gap-2">
              {isCompleted && (
                <span className="text-green-400 font-medium">Finished</span>
              )}
              {canAccess && !isCompleted && (
                <ArrowRight className="w-4 h-4 text-purple-400 group-hover:translate-x-1 transition-transform" />
              )}
              {!canAccess && (
                <Lock className="w-4 h-4 text-slate-500" />
              )}
            </div>
          </div>
        </div>

        {/* Locked overlay */}
        {!canAccess && (
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center p-6">
              <Lock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-slate-400 text-sm font-medium">Module Locked</p>
              {unlockHint && (
                <p className="text-xs text-slate-500 mt-1">{unlockHint}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default EnhancedTeenModuleCard;